import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import { supabase } from "../lib/supabaseClient";

const STATUS_OPTIONS = ["AWAITING_PAYMENT", "PAID", "SHIPPED", "CANCELLED"];

function prettyStatus(s) {
  return (
    {
      AWAITING_PAYMENT: "Awaiting payment",
      PAID: "Payment received",
      SHIPPED: "Shipped",
      CANCELLED: "Cancelled",
    }[s] || s
  );
}

const ADMIN_EMAIL = "hiradshowghi@gmail.com"; // keep outside component (stable)

function LockScreen({ message = "Locked: you don’t have access to the admin panel." }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 text-center">
        <div className="text-2xl font-semibold text-slate-900">Locked</div>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <div className="mt-4 text-xs text-slate-500">Redirecting…</div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 text-center">
        <div className="text-2xl font-semibold text-slate-900">Checking access</div>
        <p className="mt-2 text-sm text-slate-600">
          Verifying your admin session…
        </p>
        <div className="mt-4 text-xs text-slate-500">One moment…</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();

  // "checking" prevents UI flash; "locked" lets us show a screen while redirecting
  const [authStatus, setAuthStatus] = useState("checking"); // "checking" | "ok" | "locked"

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const deny = () => {
      if (!mounted) return;

      // show a locked screen immediately (better UX than flashing "No orders")
      setAuthStatus("locked");

      // required behavior
      navigate("/locked");
      return;
    };

    const allow = () => {
      if (!mounted) return;
      setAuthStatus("ok");
    };

    async function init() {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;

      // not logged in
      if (!session) {
        navigate("/admin/login");
        return;
      }

      const email = session.user?.email?.toLowerCase();

      // logged in but not admin
      if (email !== ADMIN_EMAIL) {
        deny();
        return;
      }

      allow();
    }

    init();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // logged out
      if (!session) {
        navigate("/admin/login");
        return;
      }

      const email = session.user?.email?.toLowerCase();

      // switched to non-admin
      if (email !== ADMIN_EMAIL) {
        deny();
        return;
      }

      // became admin (or stayed admin)
      allow();
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [navigate]);

  async function loadOrders() {
    setLoading(true);

    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    setLoading(false);

    if (error) {
      alert("Failed to load orders: " + error.message);
      return;
    }

    setOrders(data || []);
  }

  useEffect(() => {
    if (authStatus !== "ok") return;
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  const totalOrders = useMemo(() => orders.length, [orders]);
  const awaiting = useMemo(
    () => orders.filter((o) => o.status === "AWAITING_PAYMENT").length,
    [orders]
  );

  async function updateStatus(orderId, nextStatus) {
    const { error } = await supabase
      .from("orders")
      .update({ status: nextStatus })
      .eq("id", orderId);

    if (error) {
      alert("Update failed: " + error.message);
      return;
    }

    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: nextStatus } : o))
    );
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/admin/login");
  }

  // --- Prevent UI flash ---
  if (authStatus === "checking") return <LoadingScreen />;
  if (authStatus === "locked") return <LockScreen />;

  // --- Admin UI (only when authStatus === "ok") ---
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Admin</h1>
            <p className="mt-2 text-slate-600">Manage orders and update status.</p>
            <div className="mt-3 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{totalOrders}</span>{" "}
              orders loaded •{" "}
              <span className="font-semibold text-slate-900">{awaiting}</span>{" "}
              awaiting payment
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/shop">
              <Button variant="secondary">Back to shop</Button>
            </Link>
            <Button onClick={loadOrders} variant="secondary">
              Refresh
            </Button>
            <Button onClick={signOut}>Sign out</Button>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              No orders yet.
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div
                  key={o.id}
                  className="bg-white border border-slate-200 rounded-2xl p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-sm text-slate-600">
                        Order{" "}
                        <span className="font-semibold text-slate-900">
                          {o.order_number || o.id.slice(0, 8)}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {o.created_at
                          ? new Date(o.created_at).toLocaleString()
                          : "—"}
                      </div>
                      <div className="mt-3 text-sm text-slate-700">
                        <span className="font-semibold">{o.name}</span> •{" "}
                        <span className="underline">{o.email}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        {o.city}, {o.province} • {o.postal_code}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-slate-600">Total</div>
                      <div className="text-xl font-semibold text-slate-900">
                        ${Number(o.total || 0).toFixed(2)}
                      </div>

                      <div className="mt-3 text-xs text-slate-600">Status</div>
                      <div className="mt-1 inline-flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {prettyStatus(o.status)}
                        </span>
                      </div>

                      <select
                        value={o.status}
                        onChange={(e) => updateStatus(o.id, e.target.value)}
                        className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-300"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {prettyStatus(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <div className="text-sm font-semibold text-slate-900">
                      Items
                    </div>
                    <div className="mt-2 space-y-2">
                      {(o.items || []).map((it, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-sm"
                        >
                          <div className="text-slate-700">
                            {it.qty} × {it.name}
                          </div>
                          <div className="font-semibold text-slate-900">
                            $
                            {Number(it.lineTotal ?? it.price * it.qty).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {o.notes ? (
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                      <div className="font-semibold text-slate-900">Notes</div>
                      <div className="mt-1">{o.notes}</div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
